import { Check, ChevronLeft, ChevronRight, Lock } from 'lucide-react';

import cn from '@/utils/cn';

export interface WizardStep {
  key: number;
  title: string;
}

type StepState = 'pending' | 'active' | 'done';

interface LeadWizardStepperProps {
  steps: WizardStep[];
  activeStep: number;
  doneSteps: Set<number>;
  onGoTo: (i: number) => void;
}

export function LeadWizardStepper({
  steps,
  activeStep,
  doneSteps,
  onGoTo,
}: LeadWizardStepperProps) {
  const stateOf = (i: number): StepState => {
    if (doneSteps.has(i)) return 'done';
    if (i === activeStep) return 'active';
    return 'pending';
  };

  const canGoTo = (i: number): boolean => {
    if (i <= activeStep || doneSteps.has(i)) return true;
    for (let k = 0; k < i; k += 1) {
      if (!doneSteps.has(k)) return false;
    }
    return true;
  };

  // The compact (phone) stepper's Back/Next targets are clamped to the real
  // step range: without this, tapping Next on the last step of a confirmed
  // order (doneSteps = {0..4}) makes canGoTo(5) true and pushes activeStep to
  // 5, which LeadWizard renders as an empty body, "Step 6 of 5" and a 120% bar.
  const lastStep = steps.length - 1;
  const prevStep = Math.max(activeStep - 1, 0);
  const nextStep = Math.min(activeStep + 1, lastStep);

  const dotClass = (state: StepState): string => {
    if (state === 'done') return 'bg-emerald-500 text-white';
    if (state === 'active') return 'bg-yellow-400 text-black';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <>
      {/* Phone and tablet steppers share one wrapper so the min-[1100px] grid
          in LeadWizard gets exactly two children (this wrapper OR the vertical
          stepper, plus the step panel). Visibility is deterministic because no
          element competes on `display` across variant families: Tailwind v4
          emits arbitrary variants (min-[1100px]:) BEFORE named breakpoints
          (sm:) regardless of source order, so `sm:block min-[1100px]:hidden` on
          one element would never hide it. Plain utilities are emitted before
          all variants, so a base `hidden` always loses to its own variant. */}
      <div className="min-w-0 min-[1100px]:hidden">
        {/* Compact stepper - below sm, where a five-card rail cannot fit */}
        <div className="rounded-xl border border-border bg-card p-3 sm:hidden">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-semibold">{steps[activeStep]?.title}</p>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {`Step ${activeStep + 1} of ${steps.length}`}
            </span>
          </div>
          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={steps.length}
            aria-valuenow={activeStep + 1}
            aria-label="Onboarding progress"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-[11px] capitalize text-muted-foreground">{stateOf(activeStep)}</p>
            {/* The compact variant replaces the five step buttons, so without
                these an RM on a phone has no way back to an earlier step - the
                steps themselves only offer "Save & Continue". */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onGoTo(prevStep)}
                disabled={activeStep <= 0}
                className="inline-flex min-h-11 items-center gap-1 rounded-md border border-border px-3 text-xs font-semibold disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <button
                type="button"
                onClick={() => onGoTo(nextStep)}
                disabled={activeStep >= lastStep || !canGoTo(nextStep)}
                className="inline-flex min-h-11 items-center gap-1 rounded-md border border-border px-3 text-xs font-semibold disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Horizontal stepper - sm and up; the wrapper hides it at >=1100px */}
        <div className="hidden rounded-xl border border-border bg-card p-3 sm:block">
          <ol aria-label="Onboarding steps" className="flex items-stretch gap-2">
            {steps.map((s, i) => {
              const state = stateOf(i);
              const clickable = canGoTo(i);
              const cardClass =
                state === 'active'
                  ? 'border-primary bg-primary/5'
                  : state === 'done'
                    ? 'border-emerald-500/40 bg-emerald-500/5'
                    : 'border-border bg-background';
              return (
                <li
                  key={s.key}
                  aria-current={i === activeStep ? 'step' : undefined}
                  className="flex min-w-0 flex-1 items-center gap-2"
                >
                  <button
                    type="button"
                    onClick={() => onGoTo(i)}
                    disabled={!clickable}
                    title={!clickable ? 'Complete the current step first' : s.title}
                    className={cn(
                      'flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2.5 rounded-lg border p-2 text-left transition-colors md:justify-start',
                      cardClass,
                      clickable
                        ? 'cursor-pointer hover:border-primary/60'
                        : 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold',
                        dotClass(state),
                      )}
                    >
                      {state === 'done' ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : !clickable ? (
                        <Lock className="h-3.5 w-3.5" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span className="hidden min-w-0 flex-1 md:block">
                      <p
                        className={cn(
                          'truncate text-xs font-semibold leading-tight sm:text-sm',
                          state === 'active' && 'text-foreground',
                          state === 'pending' && 'text-muted-foreground',
                        )}
                      >
                        {s.title}
                      </p>
                      <p className="mt-0.5 truncate text-[10px] uppercase tracking-wide text-muted-foreground">
                        {state}
                      </p>
                    </span>
                  </button>
                  {i < steps.length - 1 && (
                    <div
                      className={cn(
                        'h-0.5 w-3 shrink-0 rounded-full',
                        state === 'done' ? 'bg-emerald-500' : 'bg-border',
                      )}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {/* Vertical stepper - 1100px and up */}
      <div className="hidden self-stretch overflow-auto rounded-xl border border-border bg-card p-4 min-[1100px]:block min-[1100px]:h-full min-[1100px]:min-h-0">
        <ol className="flex flex-col">
          {steps.map((s, i) => {
            const state = stateOf(i);
            const clickable = canGoTo(i);
            const lineClass =
              state === 'done'
                ? 'bg-emerald-500'
                : state === 'active'
                  ? 'bg-yellow-400'
                  : 'bg-border';
            return (
              <li key={s.key} className="flex justify-start gap-3">
                <div className="flex flex-col items-center pt-1">
                  <button
                    type="button"
                    onClick={() => onGoTo(i)}
                    disabled={!clickable}
                    title={!clickable ? 'Complete the current step first' : s.title}
                    className={cn(
                      'grid h-8 w-8 place-items-center rounded-full text-xs font-bold transition-colors',
                      dotClass(state),
                      clickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70',
                    )}
                  >
                    {state === 'done' ? (
                      <Check className="h-4 w-4" />
                    ) : !clickable ? (
                      <Lock className="h-4 w-4" />
                    ) : (
                      i + 1
                    )}
                  </button>
                  {i < steps.length - 1 && (
                    <div className={cn('my-1 min-h-[28px] w-0.5 flex-1', lineClass)} />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onGoTo(i)}
                  disabled={!clickable}
                  className={cn(
                    'block pb-6 pt-1 text-left',
                    clickable ? 'cursor-pointer' : 'cursor-not-allowed',
                  )}
                >
                  <p
                    className={cn(
                      'text-sm font-semibold leading-tight',
                      state === 'active' && 'text-foreground',
                      state === 'pending' && 'text-muted-foreground',
                    )}
                  >
                    {s.title}
                  </p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                    {state}
                  </p>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </>
  );
}
